// GridCanvas — cursor-reactive CSS-variable-driven background grid.
// Ported from South Seas. Reads Waytide theme tokens (--wt-edge for
// grid, --wt-accent for axes) and draws an axis-centred grid that
// fades radially from the mouse cursor outward.

import { useRef, useEffect, useCallback } from "react";

const GRID_SPACING = 48;
const GLOW_RADIUS = 900;
const DRAW_DEADBAND_PX = 4;
const GRID_OPACITY = 0.55;
const AXIS_OPACITY = 0.85;

interface GridVars {
  gridColor: string;
  axisColor: string;
}

let varCache: { theme: string; vars: GridVars } | null = null;

function getGridVars(themeKey: string): GridVars {
  if (varCache && varCache.theme === themeKey) return varCache.vars;
  const cs = getComputedStyle(document.documentElement);
  // Prefer the stronger edge token for the grid so it reads against
  // the surface on both light and dark themes; fall back to accent
  // when edge-strong is missing.
  const gridColor =
    cs.getPropertyValue("--wt-edge-strong").trim() ||
    cs.getPropertyValue("--wt-edge").trim() ||
    "#2a2a2a";
  const axisColor =
    cs.getPropertyValue("--wt-accent").trim() ||
    cs.getPropertyValue("--wt-edge-strong").trim() ||
    "#888";
  const vars: GridVars = { gridColor, axisColor };
  varCache = { theme: themeKey, vars };
  return vars;
}

let offscreen: HTMLCanvasElement | null = null;

function drawGrid(
  canvas: HTMLCanvasElement,
  mx: number,
  my: number,
  themeKey: string,
) {
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (w === 0 || h === 0) return;

  const pw = Math.round(w * dpr);
  const ph = Math.round(h * dpr);
  if (canvas.width !== pw || canvas.height !== ph) {
    canvas.width = pw;
    canvas.height = ph;
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  const hasCursor = mx >= 0 && my >= 0;
  if (!hasCursor) return;

  const { gridColor, axisColor } = getGridVars(themeKey);

  if (!offscreen) offscreen = document.createElement("canvas");
  const off = offscreen;
  if (off.width !== pw || off.height !== ph) {
    off.width = pw;
    off.height = ph;
  }
  const oc = off.getContext("2d");
  if (!oc) return;

  oc.setTransform(dpr, 0, 0, dpr, 0, 0);
  oc.clearRect(0, 0, w, h);

  // Read --wt-content-left + --wt-content-top so the grid axes
  // anchor at the intersection of the rail's right edge and the tab
  // bar's bottom edge — the natural origin of the chat/tab chrome.
  // Pages without a rail/tab bar leave the vars unset (0), in which
  // case the axes fall back to the viewport centre.
  const rootStyle = getComputedStyle(document.documentElement);
  const contentLeftVar = rootStyle.getPropertyValue("--wt-content-left").trim();
  const contentTopVar = rootStyle.getPropertyValue("--wt-content-top").trim();
  const contentLeft = parseFloat(contentLeftVar || "0") || 0;
  const contentTop = parseFloat(contentTopVar || "0") || 0;
  const cx = contentLeft > 0 ? Math.round(contentLeft) : Math.round(w / 2);
  const cy = contentTop > 0 ? Math.round(contentTop) : Math.round(h / 2);

  // Grid lines. Seed from the cursor so every gridline is an offset
  // of the cursor position and the axes fall on integer-multiple
  // lines. This reads as "the cursor moves the grid" rather than
  // "the cursor reveals a fixed grid", which makes the off-axis
  // lines look intentional instead of mis-aligned with the content.
  oc.globalAlpha = GRID_OPACITY;
  oc.lineWidth = 0.5;
  oc.strokeStyle = gridColor;
  oc.beginPath();
  const startX = cx - Math.ceil(cx / GRID_SPACING) * GRID_SPACING;
  for (let x = startX; x <= w + GRID_SPACING; x += GRID_SPACING) {
    if (Math.abs(x - cx) < 1) continue;
    const px = Math.round(x) + 0.5;
    oc.moveTo(px, 0);
    oc.lineTo(px, h);
  }
  const startY = cy - Math.ceil(cy / GRID_SPACING) * GRID_SPACING;
  for (let y = startY; y <= h + GRID_SPACING; y += GRID_SPACING) {
    if (Math.abs(y - cy) < 1) continue;
    const py = Math.round(y) + 0.5;
    oc.moveTo(0, py);
    oc.lineTo(w, py);
  }
  oc.stroke();

  // Axes — brighter accent, crisp 1px
  oc.globalAlpha = AXIS_OPACITY;
  oc.lineWidth = 1;
  oc.strokeStyle = axisColor;
  oc.beginPath();
  const axisX = Math.round(cx) + 0.5;
  const axisY = Math.round(cy) + 0.5;
  oc.moveTo(axisX, 0);
  oc.lineTo(axisX, h);
  oc.moveTo(0, axisY);
  oc.lineTo(w, axisY);
  oc.stroke();

  // Radial mask from cursor
  oc.globalCompositeOperation = "destination-in";
  const grad = oc.createRadialGradient(mx, my, 0, mx, my, GLOW_RADIUS);
  grad.addColorStop(0, "rgba(0,0,0,1)");
  grad.addColorStop(0.067, "rgba(0,0,0,0.8)");
  grad.addColorStop(0.1, "rgba(0,0,0,0.3)");
  grad.addColorStop(0.25, "rgba(0,0,0,0.08)");
  grad.addColorStop(0.5, "rgba(0,0,0,0.02)");
  grad.addColorStop(1, "rgba(0,0,0,0)");
  oc.fillStyle = grad;
  oc.fillRect(0, 0, w, h);
  oc.globalCompositeOperation = "source-over";

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.drawImage(off, 0, 0);
  ctx.restore();
}

export function useGridCanvas(themeDep: string) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -999, y: -999 });
  const lastDrawnRef = useRef({ x: -9999, y: -9999 });
  const rafRef = useRef<number>(0);
  const themeRef = useRef(themeDep);
  themeRef.current = themeDep;

  const draw = useCallback(() => {
    // Invalidate the var cache so theme switches pick up new colours.
    varCache = null;
    if (canvasRef.current) {
      drawGrid(
        canvasRef.current,
        mouseRef.current.x,
        mouseRef.current.y,
        themeRef.current,
      );
      lastDrawnRef.current.x = mouseRef.current.x;
      lastDrawnRef.current.y = mouseRef.current.y;
    }
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(draw));
    const onResize = () => draw();
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", onResize);
    };
  }, [draw, themeDep]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      const dx = e.clientX - lastDrawnRef.current.x;
      const dy = e.clientY - lastDrawnRef.current.y;
      if (dx * dx + dy * dy < DRAW_DEADBAND_PX * DRAW_DEADBAND_PX) return;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(draw);
    },
    [draw],
  );

  const handleMouseLeave = useCallback(() => {
    mouseRef.current.x = -999;
    mouseRef.current.y = -999;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(draw);
  }, [draw]);

  return { canvasRef, handleMouseMove, handleMouseLeave };
}
